import IBAN from 'iban'
import { z } from 'zod'

import { getRegistrationNumberFieldInvalidFormatErrorMessage } from '@features/invoices/utils/getRegistrationNumberFieldInvalidFormatErrorMessage'
import { getVatNumberFieldInvalidFormatErrorMessage } from '@features/invoices/utils/getVatNumberFieldInvalidFormatErrorMessage'
import { hasValidRegistrationNumber } from '@features/invoices/utils/hasValidRegistrationNumber'
import { hasValidVatNumber } from '@features/invoices/utils/hasValidVatNumber'
import { shouldAllowVatNumberWhenExempt } from '@features/invoices/utils/shouldAllowVatNumberWhenExempt'
import { MAX_COMPANY_NAME_LENGTH } from '@features/invoicingSettings/constants/maxCompanyNameLength'
import { FrenchLegalStatus } from '@features/invoicingSettings/enums/frenchLegalStatus'

import { GetMissingCompanyDetailsFormSchema } from '../types/getMissingCompanyDetailsFormSchema'

export const getCompanyFormValidationSchema: GetMissingCompanyDetailsFormSchema = ({ countryId, t }) => {
  return z
    .object({
      addressLine2: z.string().optional(),
      autoCompletedFields: z.record(z.string(), z.boolean()).optional(),
      bank: z.string().optional(),
      companyAddress: z.string(),
      companyCity: z
        .string()
        .trim()
        .nonempty({
          message: t(
            'external_invoices.missing_company_compliance_details.modal.form.field.address.city.error.required',
          ),
        }),
      companyName: z
        .string()
        .trim()
        .min(1, {
          message: t(
            'external_invoices.missing_company_compliance_details.modal.form.field.company_name.error.required',
          ),
        })
        .max(MAX_COMPANY_NAME_LENGTH),
      companyZipCode: z
        .string()
        .trim()
        .nonempty({
          message: t(
            'external_invoices.missing_company_compliance_details.modal.form.field.address.pobox.error.required',
          ),
        }),
      iban: z
        .string()
        .trim()
        .nonempty({
          message: t('external_invoices.missing_company_compliance_details.modal.form.field.iban.error.required'),
        })
        .transform((iban) => iban.replace(/\s/g, ''))
        .refine((iban) => !iban || IBAN.isValid(iban), {
          message: t('external_invoices.missing_company_compliance_details.modal.form.field.iban.error.invalid_format'),
        }),
      isVatExempted: z.boolean(),
      legalStatusCustom: z.string().optional(),
      legalStatus: z.nativeEnum(FrenchLegalStatus).optional(),
      registrationNo: z.string().trim(),
      sharedCapital: z.number().optional(),
      swift: z
        .string()
        .refine((swift) => !swift || (swift.length >= 8 && swift.length <= 11), {
          message: t(
            'external_invoices.missing_company_compliance_details.modal.form.field.swift_bic.error.invalid_format',
          ),
        })
        .optional(),
      vatNo: z.string().trim(),
    })
    .superRefine(({ registrationNo, vatNo, isVatExempted }, ctx) => {
      const isVatNumberValid = hasValidVatNumber(vatNo, countryId)
      const isRegistrationNumberValid = hasValidRegistrationNumber(registrationNo, countryId)
      const shouldValidateVatFormat = !isVatExempted || shouldAllowVatNumberWhenExempt(countryId)

      // Per-field format errors — only fires for fields with a value in the wrong format.
      // getCompanyMissingFields shows the tax section whenever any present field has invalid format,
      // so the user can always see and correct these errors.
      if (registrationNo && !isRegistrationNumberValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: getRegistrationNumberFieldInvalidFormatErrorMessage(countryId, t),
          path: ['registrationNo'],
        })
      }

      if (shouldValidateVatFormat && vatNo && !isVatNumberValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: getVatNumberFieldInvalidFormatErrorMessage(countryId, t),
          path: ['vatNo'],
        })
      }

      // Presence: require at least one when both are truly empty and not exempt.
      if (!isVatExempted && !registrationNo && !vatNo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['registrationNo'], message: '' })
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vatNo'], message: '' })
      }
    })
}
